/**
 * Project entity TanStack Query key factory.
 *
 * Migrated VERBATIM from `features/projects/api/query-keys.ts`.
 * Tuple shapes are unchanged — every key returned by every factory is
 * structurally identical to its pre-migration counterpart, so existing cached
 * queries keep hitting after the relocation. The shape is locked by the
 * cache-contract snapshot test in `__tests__/cache-contract.test.ts`.
 *
 * Naming conventions:
 *   - Top-level scope tag is the FIRST tuple element (`'project'`,
 *     `'projects'`, `'project-members'`, `'workspace-users'`).
 *   - List vs. detail factories follow the existing per-tag layout — see the
 *     adjacent test for the exact tuples; some sub-resources (e.g.
 *     `folders.files`, `files.versions`, `files.comments`) intentionally place
 *     the resource id BEFORE the sub-tag for legacy-cache compatibility.
 *   - Optional positional arguments (e.g. `sidebar(workspaceId, search?)`)
 *     PRESERVE the slot as `undefined` rather than omitting it, so that
 *     partial-match invalidation via `.slice(0, N)` behaves predictably.
 *
 * Public surface: re-exported from `entities/project/index.ts`. The legacy
 * `features/projects/api/query-keys.ts` shim re-exports from this module
 * until every consumer is swept off the deep path.
 */
import type { GetApiV1ProjectsData } from '@inference.engine/workspace-api-client/web';

/**
 * Query shape accepted by `PROJECTS_QUERY_KEYS.list` and `projectsListQuery`.
 * Mirrors the workspace API's list-projects query parameters (sortBy,
 * sortOrder, page, pageSize, search, status, ownerId, …) so consumers can
 * pass exactly what the underlying `getProjects` service function expects.
 */
export type ProjectsListQueryParams = NonNullable<GetApiV1ProjectsData['query']>;

/** Query shape accepted by `platformProjectsQuery` (maps to the platform's name/limit/direction). */
export interface PlatformProjectsQueryParams {
  search?: string;
  pageSize?: number;
  /** Sort direction over the platform's default `created_at` key; the platform defaults to ASC. */
  direction?: 'ASC' | 'DESC';
}

/* eslint-disable i18next/no-literal-string -- cache key segments */
export const PROJECTS_QUERY_KEYS = {
  detail: (projectId: string) => ['project', projectId] as const,
  /**
   * Deep-link resolution of a project id to its owning workspace/org
   * (`GET /projects/:id/resolve-workspace`). Shares the `['project', projectId]`
   * prefix so a detail invalidation also re-resolves.
   */
  resolution: (projectId: string) => ['project', projectId, 'resolution'] as const,
  filterOptions: (workspaceId: string) => ['projects', 'filter-options', workspaceId] as const,
  /**
   * Paginated/sorted/filtered project lists.
   *
   * The `workspaceId` is the FIRST argument so each workspace has an
   * independent cache namespace; the optional `query` carries page/sort/filter
   * state and is preserved as the 4th tuple slot (even when `undefined`) so
   * partial-match invalidation via `.slice(0, N)` is predictable.
   *
   *   List(wsId) → ['projects', 'list', wsId, undefined]
   *   List(wsId, q) → ['projects', 'list', wsId, q]
   *   Slice(0, 3) on either → ['projects', 'list', wsId] (per-workspace bust)
   *   Slice(0, 2) on either → ['projects', 'list'] (all workspaces)
   */
  list: (workspaceId: string, query?: ProjectsListQueryParams) =>
    ['projects', 'list', workspaceId, query] as const,
  /**
   * Infinite scroll variant of the projects list query.
   *
   * Returns `['projects', 'list', 'infinite', workspaceId, query]` — the
   * 'infinite' discriminator prevents cache collision with flat
   * `projectsListQuery` calls while preserving prefix-based invalidation.
   *
   * **Cache invalidation:** Mutations can invalidate BOTH list variants
   * with a single prefix:
   *   - `['projects', 'list']` → clears flat + infinite (all workspaces)
   *   - `['projects', 'list', wsId]` → clears flat + infinite (one workspace)
   *   - `['projects', 'list', 'infinite']` → clears only infinite (all workspaces)
   *
   * @see projectsInfiniteQuery - Consumer of this key factory
   * @see PROJECTS_QUERY_KEYS.list - Flat paginated list variant
   */
  infinite: (workspaceId: string, query?: Omit<ProjectsListQueryParams, 'page' | 'pageSize'>) =>
    ['projects', 'list', 'infinite', workspaceId, query] as const,
  sidebar: (workspaceId: string, search?: string) =>
    ['projects', 'sidebar', workspaceId, search] as const,
  folders: {
    list: (projectId: string, parentId?: string) =>
      ['project', projectId, 'folders', 'list', parentId ?? 'root'] as const,
    detail: (projectId: string, folderId: string) =>
      ['project', projectId, 'folders', 'detail', folderId] as const,
    files: (projectId: string, folderId: string) =>
      ['project', projectId, 'folders', folderId, 'files'] as const,
  },
  files: {
    detail: (projectId: string, fileId: string) =>
      ['project', projectId, 'files', 'detail', fileId] as const,
    versions: (projectId: string, fileId: string) =>
      ['project', projectId, 'files', fileId, 'versions'] as const,
    comments: (projectId: string, fileId: string) =>
      ['project', projectId, 'files', fileId, 'comments'] as const,
  },
  members: {
    all: ['project-members'] as const,
    byProject: (workspaceId: string, platformProjectId: string) =>
      ['project-members', workspaceId, platformProjectId] as const,
  },
  workspaceUsers: {
    all: ['workspace-users'] as const,
    search: (workspaceId: string, name?: string) => ['workspace-users', workspaceId, name] as const,
  },
} as const;

export const PLATFORM_PROJECTS_QUERY_KEYS = {
  list: (workspaceId: string, params?: PlatformProjectsQueryParams) =>
    ['platform-projects', workspaceId, params] as const,
} as const;
